import React, { useState, useEffect, useCallback, useRef } from 'react';
import useIdleTimer from './useIdleTimer';
import { logoutUser } from '../services/api';

/**
 * IdleLogoutManager
 *
 * Drop this inside your App.jsx in the authenticated route area.
 * Shows a warning modal with a live countdown, then auto-logs out.
 *
 * Props:
 *   onLogout     {function}  Your handleLogout from App.jsx
 *   timeoutMins  {number}    Idle minutes before logout  (default 15)
 *   warningMins  {number}    Warning shown N mins before (default 2)
 *   enabled      {boolean}   Only run when user is authenticated
 */
const IdleLogoutManager = ({
  onLogout,
  timeoutMins = 15,
  warningMins = 2,
  enabled = true,
}) => {
  const [showWarning, setShowWarning] = useState(false);
  const [countdown,   setCountdown]   = useState(warningMins * 60);
  const countRef = useRef(null);

  // ── countdown ticker ──────────────────────────────────────────
  const startCountdown = useCallback(() => {
    setCountdown(warningMins * 60);
    clearInterval(countRef.current);
    countRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) { clearInterval(countRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  }, [warningMins]);

  const stopCountdown = () => clearInterval(countRef.current);

  // ── idle callbacks ────────────────────────────────────────────
  const handleWarning = useCallback(() => {
    setShowWarning(true);
    startCountdown();
  }, [startCountdown]);

  const handleIdle = useCallback(async () => {
    setShowWarning(false);
    stopCountdown();
    try { await logoutUser(); } catch (_) {}
    onLogout();
  }, [onLogout]);

  const handleActive = useCallback(() => {
    setShowWarning(false);
    stopCountdown();
  }, []);

  const { reset } = useIdleTimer({
    timeoutMs : timeoutMins * 60 * 1000,
    warningMs : warningMins * 60 * 1000,
    onWarning : handleWarning,
    onIdle    : handleIdle,
    onActive  : handleActive,
    enabled,
  });

  // ── buttons ───────────────────────────────────────────────────
  const handleStayLoggedIn = () => {
    setShowWarning(false);
    stopCountdown();
    reset();
  };

  const handleLogoutNow = async () => {
    setShowWarning(false);
    stopCountdown();
    try { await logoutUser(); } catch (_) {}
    onLogout();
  };

  useEffect(() => () => stopCountdown(), []);

  if (!showWarning) return null;

  const mins = String(Math.floor(countdown / 60)).padStart(2, '0');
  const secs = String(countdown % 60).padStart(2, '0');
  const pct  = (countdown / (warningMins * 60)) * 100;
  const isUrgent = pct <= 30;

  return (
    <>
      {/* Backdrop */}
      <div style={styles.backdrop} />

      {/* Modal */}
      <div style={styles.modal}>
        {/* Warning icon */}
        <div style={styles.iconWrap}>
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none"
            stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
        </div>

        <h2 style={styles.title}>Session Expiring Soon</h2>
        <p style={styles.subtitle}>
          You've been inactive. Your session will expire in:
        </p>

        {/* SVG countdown ring */}
        <div style={styles.countdownWrap}>
          <svg width="100" height="100" style={{ transform: 'rotate(-90deg)' }}>
            <circle cx="50" cy="50" r="42" fill="none" stroke="#e5e7eb" strokeWidth="8" />
            <circle cx="50" cy="50" r="42" fill="none"
              stroke={isUrgent ? '#ef4444' : '#f59e0b'} strokeWidth="8"
              strokeDasharray={`${2 * Math.PI * 42}`}
              strokeDashoffset={`${2 * Math.PI * 42 * (1 - pct / 100)}`}
              style={{ transition: 'stroke-dashoffset 1s linear, stroke 0.5s' }}
            />
          </svg>
          <div style={styles.countdownText}>
            <span style={{ fontSize: '1.6rem', fontWeight: '800',
              color: isUrgent ? '#dc2626' : '#d97706' }}>
              {mins}:{secs}
            </span>
          </div>
        </div>

        <p style={styles.hint}>
          Click <strong>Stay Logged In</strong> to continue your session.
        </p>

        <div style={styles.btnRow}>
          <button onClick={handleStayLoggedIn} style={styles.btnStay}>
            ✅ Stay Logged In
          </button>
          <button onClick={handleLogoutNow} style={styles.btnLogout}>
            🚪 Logout Now
          </button>
        </div>
      </div>

      <style>{`
        @keyframes idleFadeIn {
          from { opacity:0; transform:translate(-50%,-48%); }
          to   { opacity:1; transform:translate(-50%,-50%); }
        }
      `}</style>
    </>
  );
};

const styles = {
  backdrop: {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.55)',
    backdropFilter: 'blur(3px)',
    zIndex: 99998,
  },
  modal: {
    position: 'fixed',
    top: '50%', left: '50%',
    transform: 'translate(-50%,-50%)',
    zIndex: 99999,
    background: '#fff',
    borderRadius: '16px',
    padding: '36px 40px',
    width: '360px',
    textAlign: 'center',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    animation: 'idleFadeIn .25s ease-out',
  },
  iconWrap: {
    width: '64px', height: '64px',
    borderRadius: '50%',
    background: '#fef3c7',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    margin: '0 auto 16px',
  },
  title: {
    margin: '0 0 8px',
    fontSize: '1.3rem', fontWeight: '800', color: '#1f2937',
  },
  subtitle: {
    margin: '0 0 20px',
    fontSize: '0.9rem', color: '#6b7280',
  },
  countdownWrap: {
    position: 'relative',
    width: '100px', height: '100px',
    margin: '0 auto 16px',
  },
  countdownText: {
    position: 'absolute', inset: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  hint: {
    margin: '0 0 24px',
    fontSize: '0.82rem', color: '#9ca3af',
  },
  btnRow: {
    display: 'flex', gap: '12px',
  },
  btnStay: {
    flex: 1, padding: '11px',
    borderRadius: '8px', border: 'none',
    background: 'linear-gradient(135deg,#0f766e,#0d9488)',
    color: '#fff', fontWeight: '700', fontSize: '0.9rem',
    cursor: 'pointer',
  },
  btnLogout: {
    flex: 1, padding: '11px',
    borderRadius: '8px',
    border: '1.5px solid #d1d5db',
    background: '#fff', color: '#374151',
    fontWeight: '600', fontSize: '0.9rem',
    cursor: 'pointer',
  },
};

export default IdleLogoutManager;